import { useQuery } from "@tanstack/react-query";
import { fetchFromApi } from "@/service/api";

/** A named person as GameSheet returns them throughout the box score. */
export type GameSheetPerson = {
  id: number | string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
};

/** One entry of a team's period-by-period score, e.g. `{ "1": 2 }` or `{ final: 5 }`. */
export type GameSheetScoreboardEntry = Record<string, number>;

export type GameSheetTeam = {
  id: number;
  title: string;
  abbreviation: string;
  logo: string | null;
  division: { id: number; title: string } | null;
  record: {
    wins: number;
    losses: number;
    ties: number;
    overtimeShootoutWins: number;
    overtimeShootoutLosses: number;
    points: number;
    gamesPlayed: number;
    goalsFor: number;
    goalsAgainst: number;
  };
  scoreboard: GameSheetScoreboardEntry[];
  powerPlays: { goals: number; opportunities: number };
};

export type GameSheetBoxScore = {
  id: number;
  status: string;
  gameType: string;
  attendance: number | null;
  arena: {
    location: string;
    startTime: string;
    endTime: string;
    duration: string;
  } | null;
  /** Regulation and overtime period lengths in minutes, e.g. `{ "1": 20, ot_1: 5 }`. */
  periods: Record<string, number> | null;
  season: { id: number; title: string } | null;
  home: GameSheetTeam;
  /** GameSheet's name for the away side — not `away`. */
  visitor: GameSheetTeam;
  threeStars: {
    first: GameSheetPerson | null;
    second: GameSheetPerson | null;
    third: GameSheetPerson | null;
  } | null;
};

/**
 * A team's season form, as `data.matchup` carries it.
 *
 * SEPARATE FROM `GameSheetTeam.record`, and the reason both exist: the box
 * score's `record` is THIS GAME'S contribution and is an EMPTY OBJECT until the
 * game is final. Reading `record.wins` before puck drop yields undefined, which
 * is how the panel came to render "undefined-undefined-undefined-undefined" on
 * every scheduled game.
 *
 * Everything here is a pre-formatted string from upstream, and nearly all of it
 * is nullable: an exhibition game returns null for `overallRecord`, `goalsFor`,
 * `goalsAgainst` and every power-play field. Treat a null as "not known yet"
 * and render a dash, never a computed value built from undefined.
 */
export type GameSheetTeamStats = {
  /** Records as "W-L-OTL-T" strings, e.g. "58-7-3-1". */
  seasonStandings: {
    thisSeasonStats: string | null;
    lastSeasonStats: string | null;
    last5SeasonStats: string | null;
    lifetimeSeasonStats: string | null;
  } | null;
  /** "Won 1" / "Lost 3", or null before a team has played. */
  streak: string | null;
  streakRecord: string | null;
  /** "3-1 W vs New Jersey Titans (2026-08-29)", or null. */
  lastGame: string | null;
  last5Overall: string | null;
  overallRecord: string | null;
  goalsFor: number | null;
  goalsAgainst: number | null;
};

export type GameSheetMatchupTeam = {
  id: number;
  name: string;
  abbr: string;
  logo: string | null;
  stats: GameSheetTeamStats | null;
};

/**
 * `teamA` is the HOME side and `teamB` the visitor, matched by id against
 * `boxscore.home` / `boxscore.visitor` rather than trusted by position.
 */
export type GameSheetMatchup = {
  teams: { teamA: GameSheetMatchupTeam; teamB: GameSheetMatchupTeam } | null;
  /** Head to head as "W-L-OTL-T" per side, for the season series line. */
  headToHead_thisSeason: { teamA: string; teamB: string } | null;
  headToHead_lastSeasonStats: { teamA: string; teamB: string } | null;
} | null;

export type GameSheetGameDetails = {
  boxScore: GameSheetBoxScore;
  matchup: GameSheetMatchup;
};

export type GameSheetBoxScoreResponse = {
  status?: string;
  data?: {
    boxscore?: GameSheetBoxScore;
    matchup?: GameSheetMatchup;
  };
};

/**
 * The GameSheet box score for a game, keyed by the analytics-side game id
 * (`game_id_gs` from the player API), fetched only when asked to.
 *
 * Uses the existing GameSheet client (`fetchFromApi`), which already carries
 * the partner API key and base URL from env — no new client, no key in this
 * hook.
 */
export function useGameSheetBoxScore(
  gameIdGs?: number | string | null,
  enabled = true,
) {
  const id = gameIdGs == null || gameIdGs === "" ? null : String(gameIdGs);

  return useQuery({
    queryKey: ["v2", "watch", "gamesheet-boxscore", id],
    enabled: Boolean(id) && enabled,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = (await fetchFromApi(
        `/nahlboxscore/${id}`,
      )) as GameSheetBoxScoreResponse;
      const boxScore = res?.data?.boxscore;
      if (!boxScore) return null;
      /*
       * `matchup` is kept, not discarded. It carries the season form the panel
       * shows before a game is final, which the box score's own `record` does
       * not have: that object is empty until the final whistle.
       */
      return { boxScore, matchup: res?.data?.matchup ?? null };
    },
  });
}
